import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { type Columns, DataTable } from '../components/ui/data-table';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { can, useMe } from '../lib/session';
import type { PurchaseOrderDetail, PurchaseOrderRow, PurchaseOrderStatus } from '../lib/types';
import { formatDateTime, formatMoney } from '../lib/utils';

const STATUSES: PurchaseOrderStatus[] = [
  'draft',
  'sent',
  'confirmed',
  'partially_received',
  'received',
  'cancelled',
];
const CARRIERS = [
  'omniva',
  'dpd',
  'latvijas_pasts',
  'lp_express',
  'venipak',
  'smartposti',
  'other',
] as const;
type Carrier = (typeof CARRIERS)[number];

const statusVariant = (status: PurchaseOrderStatus) =>
  status === 'cancelled'
    ? 'destructive'
    : status === 'received'
      ? 'success'
      : status === 'draft'
        ? 'warning'
        : 'secondary';

export function PurchaseOrdersPage() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<PurchaseOrderStatus | ''>('');
  const [openId, setOpenId] = useState<string>();
  const list = useQuery({
    queryKey: ['purchase-orders', status],
    queryFn: () =>
      api<{ items: PurchaseOrderRow[] }>(`/purchase-orders${status ? `?status=${status}` : ''}`),
  });

  const columns = useMemo<Columns<PurchaseOrderRow>>(
    () => [
      {
        id: 'number',
        header: t('po.number'),
        accessorFn: (r) => r.number,
        cell: ({ row }) => (
          <button
            type="button"
            className="font-medium hover:underline"
            onClick={() => {
              setOpenId(row.original.id);
            }}
          >
            {row.original.number}
          </button>
        ),
      },
      { id: 'type', header: t('po.type'), accessorFn: (r) => t(`po.types.${r.type}`) },
      { id: 'supplier', header: t('po.supplier'), accessorFn: (r) => r.supplierName },
      { id: 'order', header: t('po.order'), accessorFn: (r) => r.orderNumber ?? '—' },
      {
        id: 'status',
        header: t('po.status'),
        accessorFn: (r) => r.status,
        cell: ({ row }) => (
          <Badge variant={statusVariant(row.original.status)}>
            {t(`po.statuses.${row.original.status}`)}
          </Badge>
        ),
      },
      {
        id: 'total',
        header: t('po.total'),
        accessorFn: (r) => r.totalMinor,
        cell: ({ row }) =>
          formatMoney(row.original.totalMinor, row.original.currency, i18n.language),
      },
      {
        id: 'created',
        header: t('po.created'),
        accessorFn: (r) => r.createdAt,
        cell: ({ row }) => formatDateTime(row.original.createdAt, i18n.language),
      },
    ],
    [t, i18n.language],
  );

  return (
    <>
      <PageHeader title={t('po.title')} description={t('po.hint')} />
      <div className="mb-4 flex items-center gap-2">
        <Select
          aria-label={t('po.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as PurchaseOrderStatus | '');
          }}
          className="w-56"
        >
          <option value="">{t('po.allStatuses')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`po.statuses.${s}`)}
            </option>
          ))}
        </Select>
      </div>
      {list.error ? (
        <Alert variant="destructive" className="mb-4">
          {errorMessage(list.error)}
        </Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isPending}
        rowKey={(r) => r.id}
      />
      {openId ? (
        <PurchaseOrderDialog
          id={openId}
          onClose={() => {
            setOpenId(undefined);
          }}
        />
      ) : null}
    </>
  );
}

function formatAddress(address: Record<string, string | undefined>, pickupLabel: string) {
  return [
    [address.firstName, address.lastName].filter(Boolean).join(' '),
    address.line1,
    [address.postalCode, address.city].filter(Boolean).join(' '),
    address.countryCode,
    address.pickupPointId ? `${pickupLabel} ${address.pickupPointId}` : '',
  ]
    .filter(Boolean)
    .join(', ');
}

function PurchaseOrderDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const po = useQuery({
    queryKey: ['purchase-orders', 'detail', id],
    queryFn: () => api<PurchaseOrderDetail>(`/purchase-orders/${id}`),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [supplierRef, setSupplierRef] = useState('');
  const [carrier, setCarrier] = useState<Carrier>('other');
  const [tracking, setTracking] = useState('');
  const writable = can(me, 'suppliers.write');

  const act = async (path: string, body: unknown = {}) => {
    setBusy(true);
    setError(undefined);
    try {
      await api(`/purchase-orders/${id}/${path}`, { method: 'POST', body });
      await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const data = po.data;
  return (
    <Dialog
      open
      onClose={onClose}
      title={data ? `${t('po.title')} ${data.number}` : t('po.title')}
      className="max-w-2xl"
    >
      {po.error ? <Alert variant="destructive">{errorMessage(po.error)}</Alert> : null}
      {data ? (
        <div className="grid gap-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusVariant(data.status)}>{t(`po.statuses.${data.status}`)}</Badge>
            <span>{t(`po.types.${data.type}`)}</span>
            <span className="text-muted-foreground">
              {data.supplierName}
              {data.orderNumber ? ` · ${t('po.order')} ${data.orderNumber}` : ''}
              {data.supplierRef ? ` · ${t('po.supplierRef')} ${data.supplierRef}` : ''}
            </span>
          </div>
          <table className="w-full">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">{t('po.sku')}</th>
                <th>{t('po.item')}</th>
                <th className="text-right">{t('po.qty')}</th>
                <th className="text-right">{t('po.cost')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((i) => (
                <tr key={i.id} className="border-t">
                  <td className="py-1">{i.supplierSku ?? i.sku}</td>
                  <td>{i.name}</td>
                  <td className="text-right">{i.qty}</td>
                  <td className="text-right">
                    {formatMoney(i.unitCostMinor, data.currency, i18n.language)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-right font-medium">
            {t('po.total')}: {formatMoney(data.totalMinor, data.currency, i18n.language)}
          </p>
          {data.shipTo ? (
            <p className="text-muted-foreground">
              {t('po.shipTo')}: {formatAddress(data.shipTo, t('po.pickupPoint'))}
            </p>
          ) : null}
          {error ? <Alert variant="destructive">{error}</Alert> : null}
          {writable ? (
            <div className="grid gap-3 border-t pt-3">
              {data.status === 'draft' ? (
                <Button disabled={busy} onClick={() => void act('send')}>
                  {data.supplierManualOrders ? t('po.markSent') : t('po.send')}
                </Button>
              ) : null}
              {data.status === 'sent' ? (
                <div className="flex items-end gap-2">
                  <Field label={t('po.supplierRef')} className="flex-1">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        value={supplierRef}
                        onChange={(e) => {
                          setSupplierRef(e.target.value);
                        }}
                      />
                    )}
                  </Field>
                  <Button
                    disabled={busy}
                    onClick={() => void act('confirm', supplierRef ? { supplierRef } : {})}
                  >
                    {t('po.confirm')}
                  </Button>
                </div>
              ) : null}
              {data.status === 'confirmed' && data.type === 'dropship' ? (
                <div className="flex items-end gap-2">
                  <Field label={t('po.carrier')}>
                    {(fieldId) => (
                      <Select
                        id={fieldId}
                        value={carrier}
                        onChange={(e) => {
                          setCarrier(e.target.value as Carrier);
                        }}
                      >
                        {CARRIERS.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  <Field label={t('po.tracking')} className="flex-1">
                    {(fieldId) => (
                      <Input
                        id={fieldId}
                        value={tracking}
                        onChange={(e) => {
                          setTracking(e.target.value);
                        }}
                      />
                    )}
                  </Field>
                  <Button
                    disabled={busy || !tracking.trim()}
                    onClick={() =>
                      void act('shipment', { carrier, trackingNumber: tracking.trim() })
                    }
                  >
                    {t('po.shipped')}
                  </Button>
                </div>
              ) : null}
              {data.status === 'draft' || data.status === 'sent' || data.status === 'confirmed' ? (
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => void act('cancel', { reason: t('po.cancelReason') })}
                >
                  {t('po.cancel')}
                </Button>
              ) : null}
            </div>
          ) : null}
          <p className="text-xs text-muted-foreground">
            {t('po.created')}: {formatDateTime(data.createdAt, i18n.language)}
            {data.sentAt
              ? ` · ${t('po.sentAt')}: ${formatDateTime(data.sentAt, i18n.language)}`
              : ''}
          </p>
        </div>
      ) : null}
    </Dialog>
  );
}
